import React from "react";
import { Image, Tag } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { formatCurrency, getFullAwsUrl } from "../../../../helpers/utils";
import NoDataMessage from "../../../Components/NoData";
import { svg } from "../../../../Assets/svg";

interface EcommerceProductProps {
  type: "products" | "parts" | "components";
  items: any[];
  currency?: string;
  conversionRate?: number;
}

const EcommerceProduct: React.FC<EcommerceProductProps> = ({
  type,
  items = [],
  currency = "GBP",
  conversionRate = 1,
}) => {
  const getSectionMeta = () => {
    switch (type) {
      case "products":
        return {
          title: "Products",
          singularTitle: "Product",
          subtitle: "Ecommerce Products ordered through online storefront.",
          tagColor: "blue",
          tagLabel: "Product",
          emptyText: "No products included in this ecommerce order.",
        };
      case "parts":
        return {
          title: "Parts",
          singularTitle: "Part",
          subtitle:
            "Ecommerce Parts and accessories ordered through online storefront.",
          tagColor: "orange",
          tagLabel: "Part",
          emptyText: "No parts included in this ecommerce order.",
        };
      case "components":
        return {
          title: "Components",
          singularTitle: "Component",
          subtitle:
            "Ecommerce Components ordered through online storefront.",
          tagColor: "purple",
          tagLabel: "Component",
          emptyText: "No components included in this ecommerce order.",
        };
    }
  };

  const meta = getSectionMeta();

  return (
    <div className="d-flex flex-column gap-2">
      {/* Section Header */}
      <div className="d-flex align-items-center justify-content-between mb-1">
        <div>
          <h3 className="fs-4 fw-bold text-dark mb-0">
            {meta.title} ({items.length})
          </h3>
          <span className="text-muted fs-7">{meta.subtitle}</span>
        </div>
      </div>

      {/* Items List */}
      {items.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4 p-5 bg-white text-center">
          <NoDataMessage icon={svg?.noShelfProduct} text={meta.emptyText} />
        </div>
      ) : (
        items.map((item: any, idx: number) => {
          const title =
            item?.name ||
            item?.part_name ||
            item?.component_name ||
            item?.description ||
            `${meta.singularTitle} Item #${idx + 1}`;

          const photo =
            item?.image ||
            item?.photo ||
            (item?.variant_id &&
              typeof item.variant_id === "object" &&
              (item.variant_id.photo ||
                (Array.isArray(item.variant_id.images) &&
                  item.variant_id.images[0])));

          const unitPrice = Number(item?.price ?? item?.unit_price ?? 0);
          const quantity = Number(
            item?.quantity ?? item?.qty ?? item?.order_qty ?? 1,
          );
          const lineTotal =
            item?.total !== undefined && item?.total !== null
              ? Number(item.total)
              : Math.round(unitPrice * quantity * 100) / 100;

          return (
            <div
              key={item?._id || item?.inventory_id || idx}
              className="card border-0 shadow-sm rounded-4 bg-white p-3 mb-0"
              style={{
                transition: "all 0.2s ease-in-out",
              }}
            >
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                {/* Left: Thumbnail & Details */}
                <div
                  className="d-flex align-items-center gap-3 flex-grow-1"
                  style={{ minWidth: 0 }}
                >
                  {/* Photo / Thumbnail */}
                  <div
                    className="rounded-3 border overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 78,
                      height: 78,
                      minWidth: 78,
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                    }}
                  >
                    {photo ? (
                      <Image
                        src={getFullAwsUrl(photo)}
                        alt={title}
                        fallback={placeHolderImage}
                        style={{
                          width: 78,
                          height: 78,
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        preview={{
                          mask: <EyeOutlined style={{ fontSize: 16 }} />,
                        }}
                      />
                    ) : (
                      <Image
                        src={placeHolderImage}
                        alt={title}
                        preview={false}
                        style={{
                          width: 78,
                          height: 78,
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    {/* Tags row */}
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                      <Tag
                        color={meta.tagColor}
                        className="m-0 fs-8 fw-semibold px-2 py-0"
                        style={{ borderRadius: "4px" }}
                      >
                        {meta.tagLabel}
                      </Tag>
                      {item?.inventory_id && (
                        <span className="badge bg-light text-muted border px-2 py-0 fs-9">
                          Inv ID: {String(item.inventory_id).slice(-6)}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h5
                      className="fw-bold text-dark fs-6 mb-1"
                      style={{
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                      }}
                    >
                      {title}
                    </h5>

                    {/* Subtitle / Slug */}
                    {item?.slug && item?.slug !== title && (
                      <div className="text-secondary fs-7 text-truncate mb-1">
                        {item.slug}
                      </div>
                    )}

                    {/* Mobile price row */}
                    <div className="d-flex d-sm-none align-items-center gap-3 mt-1 fs-7">
                      <span className="text-muted">
                        Price:{" "}
                        <strong className="text-dark">
                          {formatCurrency(unitPrice, currency, conversionRate)}
                        </strong>
                      </span>
                      <span className="text-muted">
                        Total:{" "}
                        <strong className="text-primary">
                          {formatCurrency(lineTotal, currency, conversionRate)}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Price & Quantity Box */}
                <div className="d-flex align-items-center gap-4 flex-shrink-0 ms-md-auto justify-content-end">
                  {/* Financials (Unit Price & Total) */}
                  <div className="text-end d-none d-sm-block">
                    <div className="text-muted fs-8 text-uppercase fw-semibold">
                      Unit Price
                    </div>
                    <div className="fw-semibold text-dark fs-6">
                      {formatCurrency(unitPrice, currency, conversionRate)}
                    </div>
                    <div className="text-muted fs-8 text-uppercase fw-semibold mt-1">
                      Total
                    </div>
                    <div className="fw-bold text-primary fs-6">
                      {formatCurrency(lineTotal, currency, conversionRate)}
                    </div>
                  </div>

                  {/* Quantity Display Box */}
                  <div
                    className="text-center px-3 py-2 rounded-3 d-flex flex-column align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 120,
                      minWidth: 120,
                      height: 78,
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <div
                      className="text-secondary fs-8 text-uppercase fw-semibold mb-1"
                      style={{ letterSpacing: "0.5px" }}
                    >
                      Qty
                    </div>
                    <div className="fw-bolder fs-3 lh-1 text-dark">
                      {quantity.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default EcommerceProduct;